<script setup lang="ts">
import { ref } from 'vue';

import ProcessingPanel from './ProcessingPanel.vue';
import ScenarioPage from './ScenarioPage.vue';
import TenantWorkspace from './TenantWorkspace.vue';
import TenantsPage from './TenantsPage.vue';
import UsersPage from './UsersPage.vue';
import { api } from '../../api/client.js';
import { webAuth } from '../../auth/web-auth.js';

interface Tenant { id: string; name: string; code: string }

/**
 * Casca da administração.
 *
 * Só chega aqui quem o guard de navegação viu como admin global (WA01). Quem
 * autoriza é o servidor a cada request: esconder a tela nunca foi controle de
 * acesso.
 */
const tab = ref<'tenants' | 'users'>('tenants');
const tenant = ref<Tenant | null>(null);
const farms = ref<{ id: string; name: string }[]>([]);
const pastures = ref<{ id: string; name: string }[]>([]);
const devices = ref<{ id: string; label: string; origin: string; status: string }[]>([]);

async function selectTenant(selected: Tenant): Promise<void> {
  tenant.value = selected;
  await refreshCatalog();
}

async function refreshCatalog(): Promise<void> {
  const selected = tenant.value;
  if (selected === null) return;
  const [farmsResult, pasturesResult, devicesResult] = await Promise.all([
    api.list<{ items: { id: string; name: string }[] }>(`/admin/tenants/${selected.id}/farms?limit=100`),
    api.list<{ items: { id: string; name: string }[] }>(`/admin/tenants/${selected.id}/pastures?limit=100`),
    api.list<{ items: { id: string; label: string; origin: string; status: string }[] }>(
      `/admin/tenants/${selected.id}/devices?limit=100`,
    ),
  ]);
  farms.value = farmsResult.body.items ?? [];
  pastures.value = pasturesResult.body.items ?? [];
  devices.value = devicesResult.body.items ?? [];
}

function showTenants(): void {
  tab.value = 'tenants';
  tenant.value = null;
}
</script>

<template>
  <div class="admin">
    <nav>
      <button
        type="button"
        @click="showTenants"
      >
        Clientes
      </button>
      <button
        type="button"
        @click="tab = 'users'"
      >
        Usuários
      </button>
      <button
        type="button"
        @click="webAuth().logout()"
      >
        Sair
      </button>
    </nav>
    <UsersPage v-if="tab === 'users'" />
    <template v-else>
      <TenantsPage
        v-if="tenant === null"
        @select="selectTenant"
      />
      <template v-else>
        <TenantWorkspace
          :tenant-id="tenant.id"
          :tenant-name="tenant.name"
          :tenant-code="tenant.code"
          @catalog-changed="refreshCatalog"
        />
        <ScenarioPage
          :tenant-id="tenant.id"
          :tenant-name="tenant.name"
          :farms="farms"
          :pastures="pastures"
          :devices="devices"
        />
        <ProcessingPanel :tenant-id="tenant.id" />
      </template>
    </template>
  </div>
</template>

<style scoped>
.admin { display: grid; gap: 1.5rem; }
nav { display: flex; gap: .75rem; }
</style>
